import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { hasPermission } from '@lognai/plugin-sdk';
import { Tabs, Checkbox } from '@lognai/synapse-design-system-v2';

import { useEntitledPlugins } from '../../hooks/useEntitledPlugins';
import { RADAR_MODES, resolveAvailableRadarModes, type RadarMode } from './radar-modes';

/**
 * Both modes render at exactly this height so the frame never jumps on toggle.
 * Matches `RadarMap`'s own DEFAULT_HEIGHT, so the dashboard tile and the full
 * /shipments/radar page agree on one number rather than drifting apart.
 */
const MAP_HEIGHT = 560;


// Chunk boundaries. The unselected mode's code is never downloaded — the old
// tabs statically imported GlobalInsightsPanel, so Leaflet and the risk map
// shipped to every user, including those who could never open the tab.
const ShipmentRadarTile = lazy(() => import('./ShipmentRadarTile'));
const GlobalRadarTile = lazy(() => import('./GlobalRadarTile'));

const TileSkeleton: React.FC<{ height: number }> = ({ height }) => (
  <div className="animate-pulse bg-muted" style={{ height }} />
);

export interface RadarContentShape { state: 'unknown' | 'loading' | 'error' | 'empty' | 'populated'; itemCount?: number }
export interface RadarSectionProps {
  height?: number;
  embedded?: boolean;
  className?: string;
  onContentChange?: (content: RadarContentShape) => void;
}

/**
 * The dashboard's situational-awareness frame: one map, two layers.
 *
 * The inactive tile is unmounted rather than hidden. This is a correctness
 * requirement: Leaflet measures zero inside a display:none container and paints
 * a permanent grey box. GlobalRiskMap carries a MapInvalidator to survive that;
 * RadarMap has no such defence. Conditional rendering means every map mounts
 * into an already-visible, correctly-sized box. React Query caches both
 * datasets, so a toggle costs a Leaflet re-init, not a refetch.
 */
export const RadarSection: React.FC<RadarSectionProps> = ({
  height = MAP_HEIGHT,
  embedded = false,
  className = '',
  onContentChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [watchlistOnly, setWatchlistOnly] = useState(false);
  const { hasPlugin } = useEntitledPlugins();

  // Permission AND entitlement, per mode — see radar-modes.ts for why both.
  const available = useMemo(
    () => resolveAvailableRadarModes(hasPermission, hasPlugin),
    [hasPlugin]
  );

  // `?tab=global` is the retired tab param. Honour inbound links, never re-emit it.
  const requested: RadarMode | null =
    searchParams.get('radar') === 'global' || searchParams.get('tab') === 'global'
      ? 'global'
      : searchParams.get('radar') === 'shipment'
        ? 'shipment'
        : null;

  // A toggle must never offer a mode that would render a permission error.
  const mode: RadarMode =
    requested && available.includes(requested) ? requested : (available[0] as RadarMode);

  useEffect(() => {
    if (mode === 'global') onContentChange?.({ state: 'unknown' });
  }, [mode, onContentChange]);
  if (available.length === 0) return null;

  const selectMode = (next: RadarMode) => {
    const params = new URLSearchParams(searchParams);
    params.delete('tab');
    if (next === 'shipment') params.delete('radar');
    else params.set('radar', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden bg-surface ${
        embedded ? '' : 'rounded-xl border border-border'
      } ${className}`}
    >
      <Tabs
        value={mode}
        onValueChange={(value) => selectMode(value as RadarMode)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="flex min-h-11 shrink-0 items-end justify-between gap-3 border-b border-border px-4">
          <Tabs.List aria-label="Radar layer" className="border-b-0">
            {available.map((m) => (
              <Tabs.Trigger
                key={m}
                value={m}
                className="px-1.5 pb-3 pt-2 sm:px-3"
              >
                {RADAR_MODES[m].label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          {mode === 'shipment' && (
            <div className="flex shrink-0 items-center gap-4 pb-3">
              <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-foreground">
                <Checkbox
                  checked={watchlistOnly}
                  onCheckedChange={(checked) => setWatchlistOnly(checked === true)}
                  aria-label="Watchlist only"
                />
                <span aria-hidden="true">Watchlist only</span>
              </label>
              <Link to="/shipments/radar" className="text-sm text-primary hover:underline">
                View full radar →
              </Link>
            </div>
          )}
        </div>

        <Tabs.Content value={mode} className="m-0 min-h-0 flex-1">
          <Suspense fallback={<TileSkeleton height={height} />}>
            {mode === 'shipment' ? (
              <ShipmentRadarTile height={height} watchlistOnly={watchlistOnly} onContentChange={onContentChange} />
            ) : (
              <GlobalRadarTile height={height} />
            )}
          </Suspense>
        </Tabs.Content>
      </Tabs>
    </section>
  );
};
